import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, CalendarClock, HelpCircle, PackageCheck, Undo2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  PageHeader,
  LoadingState,
  KPIGrid,
  MetricCard,
  DataTable,
  TableFilters,
  type Column,
} from '@/components/common';
import { DataTableRowActions } from '@/components/common/data-table/DataTableRowActions';
import { stagger, fadeUp } from '@/lib/animations';
import {
  deploymentApi,
  type DeployedAsset,
  type DeployedSummary,
  type DeploymentStatus,
} from '@/api/services/deployment.api';
import { getErrorMessage } from '@/api/client';
import { formatDate } from '@/lib/utils';

/**
 * What we have sent out to a job and not had back.
 *
 * An asset at a wellsite after the job finished earns nothing and is not
 * available to the next job. One that is never coming back is recoverable from
 * the operator only if somebody recorded that while the job was open.
 *
 * Same shape as Rentals and treated the same way: a table rather than cards,
 * tabs that split what needs chasing from the full set, and the prose the API
 * returns kept out of the cells — "14 days overdue" scans where a sentence does
 * not. The sentence stays in the API for the alert.
 *
 * Assets nobody gave a return date sit directly beneath the late ones rather
 * than among the fine. They cannot be late, because nothing has said what late
 * would mean, and that is precisely why they go missing.
 */

const STATUS: Record<DeploymentStatus, { label: string; className: string }> = {
  OVERDUE: {
    label: 'Overdue',
    className: 'bg-status-destructive-subtle text-status-destructive-foreground',
  },
  UNSCHEDULED: {
    // Dashed with a ground of its own — a bare text colour loses to Badge's
    // default background, which is how the rentals page shipped an unreadable
    // block the first time.
    label: 'No return date',
    className:
      'border border-dashed border-status-warning bg-background text-status-warning-foreground',
  },
  OUT: { label: 'Out', className: 'bg-status-info-subtle text-status-info-foreground' },
  LOST_IN_HOLE: {
    label: 'Lost in hole',
    className: 'bg-status-neutral-subtle text-status-neutral-foreground',
  },
  RETURNED: {
    label: 'Returned',
    className: 'bg-status-success-subtle text-status-success-foreground',
  },
};

const STATUS_FILTER_OPTIONS = [
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'UNSCHEDULED', label: 'No return date' },
  { value: 'OUT', label: 'Out' },
  { value: 'LOST_IN_HOLE', label: 'Lost in hole' },
];

/** The quantified line beneath the badge. Prose is for the alert, not the cell. */
function statusDetail(asset: DeployedAsset): string {
  if (asset.daysOverdue != null) return `${asset.daysOverdue} days overdue`;
  if (asset.status === 'UNSCHEDULED') return 'no date set';
  if (asset.status === 'LOST_IN_HOLE') return 'not expected back';
  return `${asset.daysOut} days out`;
}

export default function AssetsOnJobs() {
  const [assets, setAssets] = useState<DeployedAsset[]>([]);
  const [returned, setReturned] = useState<DeployedAsset[]>([]);
  const [summary, setSummary] = useState<DeployedSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('attention');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Seeded from the URL so a link from a job's readiness panel arrives with
  // that job already selected rather than on the whole list.
  const [searchParams] = useSearchParams();
  const [jobFilter, setJobFilter] = useState(() => searchParams.get('jobId') ?? 'all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lostFor, setLostFor] = useState<DeployedAsset | null>(null);
  const [lostNote, setLostNote] = useState('');
  const [dueFor, setDueFor] = useState<DeployedAsset | null>(null);
  const [dueDate, setDueDate] = useState('');

  const load = useCallback(
    () =>
      Promise.all([
        deploymentApi.getDeployed(),
        deploymentApi.getDeployed(true),
        deploymentApi.getSummary(),
      ])
        .then(([out, all, s]) => {
          setAssets(out);
          setReturned(all.filter((a) => a.status === 'RETURNED'));
          setSummary(s);
          setSelectedIds([]);
        })
        .catch((error) => toast.error(getErrorMessage(error)))
        .finally(() => setLoading(false)),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const jobOptions = useMemo(
    () =>
      [...new Map([...assets, ...returned].map((a) => [a.jobId, a.jobNumber])).entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([id, number]) => ({ value: id, label: number })),
    [assets, returned]
  );

  const applyFilters = (rows: DeployedAsset[]) =>
    rows.filter((a) => {
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q ||
        a.assetNumber.toLowerCase().includes(q) ||
        a.name.toLowerCase().includes(q) ||
        a.jobNumber.toLowerCase().includes(q) ||
        (a.wellName ?? '').toLowerCase().includes(q);
      return (
        matchesSearch &&
        (statusFilter === 'all' || a.status === statusFilter) &&
        (jobFilter === 'all' || a.jobId === jobFilter)
      );
    });

  const recordReturn = async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id) => deploymentApi.recordReturn(id)));
      toast.success(
        ids.length === 1 ? 'Recorded as back' : `${ids.length} assets recorded as back`
      );
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const saveLostInHole = async () => {
    if (!lostFor) return;
    try {
      await deploymentApi.recordLostInHole(lostFor.id, lostNote || undefined);
      toast.success(`${lostFor.assetNumber} recorded as lost in hole`);
      setLostFor(null);
      setLostNote('');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const saveDueDate = async () => {
    if (!dueFor) return;
    try {
      await deploymentApi.setExpectedReturn(dueFor.id, dueDate || null);
      toast.success(dueDate ? `${dueFor.assetNumber} due ${formatDate(dueDate)}` : 'Date cleared');
      setDueFor(null);
      setDueDate('');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const assetColumn: Column<DeployedAsset> = {
    key: 'assetNumber',
    header: 'Asset',
    sortable: true,
    linkTo: (a) => `/equipment/${a.equipmentId}`,
    render: (a) => (
      <div className="min-w-0">
        <p className="font-medium">{a.assetNumber}</p>
        <p className="text-xs text-muted-foreground">{a.name}</p>
      </div>
    ),
  };

  const jobColumn: Column<DeployedAsset> = {
    key: 'jobNumber',
    header: 'On job',
    sortable: true,
    render: (a) => (
      <div className="min-w-0">
        <Link
          to={`/jobs/${a.jobId}`}
          className="underline-offset-4 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {a.jobNumber}
        </Link>
        <p className="text-xs text-muted-foreground">
          {a.wellName ?? a.jobName} · {a.jobStatus.toLowerCase()}
        </p>
      </div>
    ),
  };

  const outColumns: Column<DeployedAsset>[] = [
    assetColumn,
    jobColumn,
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (a) => (
        <div className="min-w-0">
          <Badge className={STATUS[a.status].className}>{STATUS[a.status].label}</Badge>
          <p className="mt-1 text-xs text-muted-foreground">{statusDetail(a)}</p>
        </div>
      ),
    },
    {
      key: 'assignedAt',
      header: 'Out since',
      sortable: true,
      render: (a) => (
        <div>
          <p className="tabular-nums">{formatDate(a.assignedAt)}</p>
          <p className="text-xs text-muted-foreground">{a.daysOut} days</p>
        </div>
      ),
    },
    {
      key: 'expectedReturnAt',
      header: 'Due back',
      sortable: true,
      render: (a) =>
        a.expectedReturnAt ? (
          <span
            className={`tabular-nums ${a.daysOverdue != null ? 'text-status-destructive-foreground' : ''}`}
          >
            {formatDate(a.expectedReturnAt)}
          </span>
        ) : (
          // Not a dash. An asset nobody dated is the one that stays out.
          <span className="text-status-warning-foreground">Not set</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (a) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              recordReturn([a.id]);
            }}
          >
            <Undo2 className="mr-1.5 h-3.5 w-3.5" />
            Record return
          </Button>
          {/* The two rarer actions behind a menu. Three buttons in a cell is a
              row nobody can scan. */}
          <DataTableRowActions
            row={a}
            actions={[
              {
                label: a.expectedReturnAt ? 'Change return date' : 'Set return date',
                icon: CalendarClock,
                onClick: (row) => {
                  setDueFor(row);
                  setDueDate(row.expectedReturnAt?.slice(0, 10) ?? '');
                },
              },
              {
                label: 'Record lost in hole',
                icon: AlertTriangle,
                destructive: true,
                separator: true,
                onClick: (row) => {
                  setLostFor(row);
                  setLostNote('');
                },
              },
            ]}
          />
        </div>
      ),
    },
  ];

  // Status and Due back say nothing once an asset is back. How long it was out
  // is what somebody checks against a day rate or a job's costs.
  const returnedColumns: Column<DeployedAsset>[] = [
    assetColumn,
    jobColumn,
    {
      key: 'assignedAt',
      header: 'Out since',
      sortable: true,
      render: (a) => <span className="tabular-nums">{formatDate(a.assignedAt)}</span>,
    },
    {
      key: 'daysOut',
      header: 'Days out',
      sortable: true,
      className: 'text-right',
      render: (a) => <span className="tabular-nums">{a.daysOut}</span>,
    },
  ];

  if (loading) return <LoadingState variant="detail" />;

  const needsChasing = applyFilters(
    assets.filter((a) => a.status === 'OVERDUE' || a.status === 'UNSCHEDULED')
  );
  const allOut = applyFilters(assets);
  const returnedRows = applyFilters(returned);

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Equipment', href: '/equipment' },
            { label: 'Out on Jobs' },
          ]}
          title="Out on Jobs"
          description="Company-owned assets issued to a job and not yet returned, ordered by days overdue."
        />
      </motion.div>

      {summary && (
        <motion.div variants={fadeUp}>
          <KPIGrid columns={4}>
            <MetricCard
              label="Out"
              value={summary.out}
              icon={<PackageCheck className="h-5 w-5" />}
              sentiment={summary.out > 0 ? 'info' : 'success'}
              subtitle="assigned and not returned"
            />
            <MetricCard
              label="Overdue"
              value={summary.overdue}
              icon={<AlertTriangle className="h-5 w-5" />}
              sentiment={summary.overdue > 0 ? 'danger' : 'success'}
              subtitle={
                summary.worstOverdueDays > 0
                  ? `worst is ${summary.worstOverdueDays} days late`
                  : 'nothing past its date'
              }
            />
            <MetricCard
              label="No return date"
              value={summary.unscheduled}
              icon={<HelpCircle className="h-5 w-5" />}
              sentiment={summary.unscheduled > 0 ? 'warning' : 'success'}
              subtitle="cannot be called late"
            />
            <MetricCard
              label="Lost in hole"
              value={summary.lostInHole}
              icon={<Undo2 className="h-5 w-5" />}
              sentiment="neutral"
              subtitle="recorded, not expected back"
            />
          </KPIGrid>
        </motion.div>
      )}

      <motion.div variants={fadeUp}>
        <Tabs
          value={tab}
          onValueChange={(next) => {
            setTab(next);
            setSelectedIds([]);
          }}
          className="w-full"
        >
          <TabsList>
            <TabsTrigger value="attention">Needs chasing ({needsChasing.length})</TabsTrigger>
            <TabsTrigger value="out">All out ({allOut.length})</TabsTrigger>
            <TabsTrigger value="returned">Returned ({returnedRows.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="attention" className="mt-6">
            <DataTable<DeployedAsset>
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search asset, job or well..."
                  filters={[
                    {
                      id: 'status',
                      label: 'Statuses',
                      value: statusFilter,
                      options: STATUS_FILTER_OPTIONS,
                      onChange: setStatusFilter,
                      placeholder: 'Status',
                    },
                    {
                      id: 'job',
                      label: 'Jobs',
                      value: jobFilter,
                      options: jobOptions,
                      onChange: setJobFilter,
                      placeholder: 'Job',
                    },
                  ]}
                />
              }
              data={needsChasing}
              columns={outColumns}
              pagination
              exportable
              exportFilename="assets-to-chase"
              columnToggle
              selectable
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              getRowId={(a) => a.id}
              emptyTitle="Nothing to chase"
              emptyDescription="Everything out is inside its return date."
            />
          </TabsContent>

          <TabsContent value="out" className="mt-6">
            <DataTable<DeployedAsset>
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search asset, job or well..."
                  filters={[
                    {
                      id: 'status',
                      label: 'Statuses',
                      value: statusFilter,
                      options: STATUS_FILTER_OPTIONS,
                      onChange: setStatusFilter,
                      placeholder: 'Status',
                    },
                    {
                      id: 'job',
                      label: 'Jobs',
                      value: jobFilter,
                      options: jobOptions,
                      onChange: setJobFilter,
                      placeholder: 'Job',
                    },
                  ]}
                />
              }
              data={allOut}
              columns={outColumns}
              pagination
              exportable
              exportFilename="assets-out-on-jobs"
              columnToggle
              selectable
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              getRowId={(a) => a.id}
              emptyTitle="Nothing is out"
              emptyDescription="Every asset committed to a job has been returned."
            />
          </TabsContent>

          <TabsContent value="returned" className="mt-6">
            <DataTable<DeployedAsset>
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search asset, job or well..."
                  filters={[
                    {
                      id: 'status',
                      label: 'Statuses',
                      value: statusFilter,
                      options: STATUS_FILTER_OPTIONS,
                      onChange: setStatusFilter,
                      placeholder: 'Status',
                    },
                    {
                      id: 'job',
                      label: 'Jobs',
                      value: jobFilter,
                      options: jobOptions,
                      onChange: setJobFilter,
                      placeholder: 'Job',
                    },
                  ]}
                />
              }
              data={returnedRows}
              columns={returnedColumns}
              pagination
              exportable
              exportFilename="assets-returned"
              columnToggle
              emptyTitle="Nothing returned yet"
              emptyDescription="Assets appear here once a return is recorded."
            />
          </TabsContent>
        </Tabs>
      </motion.div>

      {selectedIds.length > 0 && tab !== 'returned' && (
        <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border bg-card p-3 shadow-floating">
          <span className="pl-1 text-sm font-medium">
            {selectedIds.length} asset{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <Button size="sm" onClick={() => recordReturn(selectedIds)}>
            <Undo2 className="mr-1.5 h-3.5 w-3.5" />
            Record return
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
            Clear
          </Button>
        </div>
      )}

      <Dialog open={!!lostFor} onOpenChange={(open) => !open && setLostFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record {lostFor?.assetNumber} as lost in hole</DialogTitle>
            <DialogDescription>
              Lost in hole is recoverable from the operator when it is documented at the time and
              absorbed when it is not. What you write here is the claim.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="lostNote">What happened</Label>
            <Textarea
              id="lostNote"
              value={lostNote}
              onChange={(e) => setLostNote(e.target.value)}
              placeholder="Stuck at 8,400 ft, fishing attempts abandoned on the operator's instruction"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLostFor(null)}>
              Cancel
            </Button>
            <Button onClick={saveLostInHole}>Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dueFor} onOpenChange={(open) => !open && setDueFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>When is {dueFor?.assetNumber} due back?</DialogTitle>
            <DialogDescription>
              An asset with no return date cannot be reported late, which is how one sits in a yard
              for weeks without anybody noticing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="dueDate">Expected return</Label>
            <Input
              id="dueDate"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDueFor(null)}>
              Cancel
            </Button>
            <Button onClick={saveDueDate}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
